import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Ad {
  id: string;
  titulo: string;
  subtitulo?: string;
  descricao?: string;
  imagem?: string;
  mimeType?: string;
  linkUrl?: string;
  linkTexto?: string;
}

const apiUrl = () => process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';

/** Visualizações e cliques (o servidor conta no máximo uma vez a cada 10 min por IP). */
const track = (id: string, tipo: 'view' | 'click') => {
  fetch(`${apiUrl()}/v1/public/publicidade/${encodeURIComponent(id)}/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tipo }),
    keepalive: true,
  }).catch(() => {});
};

interface AdCardsProps {
  /** Código do spot. Por omissão SRVCS (geral); as páginas de categoria usam CAT<id>. */
  spot?: string;
  limit?: number;
  /** Faixa 3:1 (áreas das páginas de categoria, geridas em Admin › Publicidade › App). */
  banner?: boolean;
}

const AdCards: React.FC<AdCardsProps> = ({ spot = 'SRVCS', limit = 2, banner = false }) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState<Ad[]>([]);

  useEffect(() => {
    let cancelado = false;
    setAds([]);
    const fetchAds = async () => {
      try {
        const response = await fetch(`${apiUrl()}/v1/public/publicidade?spot=${encodeURIComponent(spot)}&limit=${limit}`);
        const data = await response.json();
        if (!cancelado && data.success && data.data?.length) {
          const lista: Ad[] = data.data.slice(0, limit);
          setAds(lista);
          lista.forEach((a) => track(a.id, 'view'));
        }
      } catch (error) {
        console.error('Error fetching ads:', error);
      }
    };
    fetchAds();
    return () => { cancelado = true; };
  }, [spot, limit]);

  if (ads.length === 0) return null;

  return (
    <div className={`grid grid-cols-1 ${ads.length > 1 ? 'sm:grid-cols-2' : ''} gap-4 mt-6`}>
      {ads.map((ad) => {
        const isExternal = ad.linkUrl?.startsWith('http');
        const handleAdClick = () => {
          if (!ad.linkUrl) return;
          track(ad.id, 'click');
          if (isExternal) {
            window.open(ad.linkUrl, '_blank', 'noopener,noreferrer');
          } else {
            navigate(ad.linkUrl);
          }
        };
        return (
        <div
          key={ad.id}
          onClick={handleAdClick}
          className="block rounded-2xl overflow-hidden border border-pagali-gray-100 hover:shadow-lg transition-all group cursor-pointer"
        >
          {ad.imagem && (
            <div className={`${banner ? 'aspect-[3/1]' : 'aspect-video'} overflow-hidden bg-pagali-gray-50`}>
              <img
                src={ad.imagem.startsWith('data:') || ad.imagem.startsWith('http') ? ad.imagem : `data:${ad.mimeType || 'image/png'};base64,${ad.imagem}`}
                alt={ad.titulo}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <div className="p-4 bg-white">
            <h4 className="font-semibold text-pagali-gray-800 text-sm truncate">{ad.titulo}</h4>
            {ad.subtitulo && (
              <p className="text-xs text-pagali-gray-500 mt-1 truncate">{ad.subtitulo}</p>
            )}
            {ad.descricao && (
              <p className="text-xs text-pagali-gray-500 mt-1 line-clamp-2">{ad.descricao}</p>
            )}
            {ad.linkTexto && (
              <span className="text-xs text-pagali-primary font-medium mt-2 inline-block">
                {ad.linkTexto} →
              </span>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default AdCards;
